import type { ChangeEvent, MouseEvent } from 'react';
import { useMemo, useState } from 'react';

import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { useRecoilValueLoadable } from 'recoil';

import { CenteredText } from '../../../components/base';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { SearchInput } from '../../../shared/ui';
import { NotificationType, useShowNotification } from '../../../state/notification';
import { userState } from '../../../state/user';
import { getAiIncidentUserName } from '../lib/getAiIncidentUserName';
import { getComparator, type Order, type OrderBy } from '../lib/sortTable';
import type { AiIncident, AiIncidentForm } from '../model/types';
import { useAiIncidents } from '../model/useAiIncidents';
import { useConvertAiIncidentToIncident } from '../model/useConvertAiIncidentToIncident';
import { useDeleteAiIncident } from '../model/useDeleteAiIncident';
import { useUpdateAiIncidentViewed } from '../model/useUpdateAiIncidentViewed';
import { AiIncidentConfirmDialog } from './AiIncidentConfirmDialog';
import { AiIncidentsTableRow } from './AiIncidentsTableRow';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;

export const AiIncidentsTable = () => {
  const { t } = useTranslation();
  const currentUser = useRecoilValueLoadable(userState).valueMaybe();
  const eventId = currentUser?.currentEvent?.id ?? 0;
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('id');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [confirmAiIncident, setConfirmAiIncident] = useState<AiIncident | undefined>();
  const { data, isLoading, isError } = useAiIncidents({ eventId, page, pageSize, enabled: Boolean(eventId) });
  const { mutateAsync: deleteAiIncident, isPending: isDeleteLoading } = useDeleteAiIncident();
  const { mutateAsync: convertAiIncidentToIncident, isPending: isConfirmLoading } = useConvertAiIncidentToIncident();
  const { mutate: updateAiIncidentViewed } = useUpdateAiIncidentViewed();
  const { execute: showNotification } = useShowNotification();
  const { showConfirm, confirmModal } = useConfirmDialog({
    title: t('notifications.removeAiIncident.title'),
    content: t('notifications.removeAiIncident.content'),
  });
  const aiIncidents = useMemo(() => data?.data ?? [], [data?.data]);

  const handleRequestSort = (event: MouseEvent<unknown>, property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(DEFAULT_PAGE);
  };

  const handleResetSearch = () => {
    setSearch('');
    setPage(DEFAULT_PAGE);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(DEFAULT_PAGE);
  };

  const handleDeleteAiIncident = async (aiIncident: AiIncident) => {
    const isConfirmed = await showConfirm();

    if (!isConfirmed) {
      return;
    }

    await deleteAiIncident(aiIncident.id);
    showNotification({ type: NotificationType.SUCCESS, text: t('ai-incident-delete-success') });
  };

  const handleOpenConfirmAiIncident = (aiIncident: AiIncident) => {
    setConfirmAiIncident(aiIncident);

    if (!aiIncident.isViewed) {
      updateAiIncidentViewed(aiIncident.id);
    }
  };

  const handleCloseConfirmAiIncident = () => {
    setConfirmAiIncident(undefined);
  };

  const handleConfirmAiIncident = async (aiIncident: AiIncident, values: AiIncidentForm) => {
    const event = aiIncident.event?.id ?? eventId;

    if (!event) {
      return;
    }

    await convertAiIncidentToIncident({
      id: aiIncident.id,
      data: {
        title: values.title,
        description: values.description,
        status: values.status,
        resolutionLevel: values.resolutionLevel,
        responsible: values.responsible?.id ?? null,
        control: values.control?.id ?? null,
        participants: values.participants.map(({ id }) => id),
        userFunction: values.userFunction?.id ?? null,
        location: values.location?.id ?? null,
        locationDescription: values.locationDescription,
        endDatetime: values.endDatetime,
        isResponsibleInformed: values.isResponsibleInformed,
        isUrgent: values.isUrgent,
        photoSources: values.photoSources,
      },
    });
    showNotification({ type: NotificationType.SUCCESS, text: t('ai-incident-confirm-success') });
  };

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search],
  );

  const rows = useMemo(
    () =>
      [...aiIncidents].sort(getComparator(order, orderBy)).filter((aiIncident) => {
        const searchValue = [
          aiIncident.title,
          aiIncident.description,
          aiIncident.source,
          aiIncident.location?.title,
          aiIncident.userFunction?.name,
          getAiIncidentUserName(aiIncident.registerBy),
          getAiIncidentUserName(aiIncident.responsible),
          getAiIncidentUserName(aiIncident.control),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchValue.includes(debouncedSearch.toLowerCase());
      }),
    [aiIncidents, debouncedSearch, order, orderBy],
  );
  const pagination = data?.meta.pagination;

  return (
    <>
      <Box display='flex' alignItems='center' pl={2} pt={2} pr={2}>
        <Box marginLeft='auto'>
          <SearchInput value={search} onChange={handleSearch} placeholder={t('search')} onReset={handleResetSearch} />
        </Box>
      </Box>
      {isLoading && (
        <CenteredText sx={{ marginTop: '50px' }}>
          <CircularProgress />
        </CenteredText>
      )}
      {isError && !isLoading && (
        <CenteredText sx={{ marginTop: '50px' }}>
          <Trans i18nKey='ai-incidents-load-error' />
        </CenteredText>
      )}
      {!isLoading && !isError && aiIncidents.length > 0 && (
        <Box p={2}>
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={orderBy === 'id' ? order : false}>
                      <TableSortLabel
                        onClick={(event) => handleRequestSort(event, 'id')}
                        direction={orderBy === 'id' ? order : 'asc'}
                      >
                        <Trans i18nKey='id' />
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy === 'title' ? order : false}>
                      <TableSortLabel
                        onClick={(event) => handleRequestSort(event, 'title')}
                        direction={orderBy === 'title' ? order : 'asc'}
                      >
                        <Trans i18nKey='title' />
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <Trans i18nKey='description' />
                    </TableCell>

                    <TableCell>
                      <Trans i18nKey='location' />
                    </TableCell>
                    <TableCell>
                      <Trans i18nKey='responsible' />
                    </TableCell>
                    <TableCell>
                      <Trans i18nKey='control' />
                    </TableCell>
                    <TableCell>
                      <Trans i18nKey='user-function' />
                    </TableCell>
                    <TableCell sortDirection={orderBy === 'resolutionLevel' ? order : false}>
                      <TableSortLabel
                        onClick={(event) => handleRequestSort(event, 'resolutionLevel')}
                        direction={orderBy === 'resolutionLevel' ? order : 'asc'}
                      >
                        <Trans i18nKey='resolution-level' />
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <Trans i18nKey='by' />
                    </TableCell>

                    <TableCell sortDirection={orderBy === 'createdAt' ? order : false}>
                      <TableSortLabel
                        onClick={(event) => handleRequestSort(event, 'createdAt')}
                        direction={orderBy === 'createdAt' ? order : 'asc'}
                      >
                        <Trans i18nKey='date' />
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <Trans i18nKey='source' />
                    </TableCell>
                    <TableCell>
                      <Trans i18nKey='certainty' />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((aiIncident) => (
                    <AiIncidentsTableRow
                      aiIncident={aiIncident}
                      isDeleteLoading={isDeleteLoading}
                      key={aiIncident.id}
                      onConfirm={handleOpenConfirmAiIncident}
                      onDelete={handleDeleteAiIncident}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {pagination && (
              <TablePagination
                component='div'
                showFirstButton
                showLastButton
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Paper>
        </Box>
      )}
      {!isLoading && !isError && aiIncidents.length === 0 && (
        <CenteredText sx={{ marginTop: '50px' }}>
          <Trans i18nKey='ai-incidents-list-is-empty' />
        </CenteredText>
      )}
      {confirmModal}
      <AiIncidentConfirmDialog
        aiIncident={confirmAiIncident}
        isConfirmLoading={isConfirmLoading}
        open={Boolean(confirmAiIncident)}
        onClose={handleCloseConfirmAiIncident}
        onConfirm={handleConfirmAiIncident}
      />
    </>
  );
};
